import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import SiteAnalytics, { siteAnalyticsCss } from './admin/SiteAnalytics';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const PLATFORMS = [
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'facebook', label: 'Messenger' },
  { key: 'shopify', label: 'Shopify' },
];
const TABS = [
  { key: 'workspaces', label: 'Workspaces' },
  { key: 'analytics', label: 'Website analytics' },
];
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

const Copy = ({ text, label = 'Copy' }) => {
  const [done, setDone] = useState(false);
  return (
    <button type="button" className="ma-copy" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(text || ''); setDone(true); setTimeout(() => setDone(false), 1400); }}>
      {done ? 'Copied' : label}
    </button>
  );
};

const MasterAdmin = () => {
  const [tab, setTab] = useState(() => (window.location.hash === '#analytics' ? 'analytics' : 'workspaces'));
  const [workspaces, setWorkspaces] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [name, setName] = useState('');
  const [created, setCreated] = useState(null);       // { email, password, name }
  const [createErr, setCreateErr] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState('');               // expanded ws._id
  const [section, setSection] = useState({});         // ws._id -> 'access' | 'settings' | 'shopify'
  const [reveal, setReveal] = useState({});           // ws._id -> show password
  const [shopifyByWs, setShopifyByWs] = useState({}); // ws._id -> { limit, callbackUrl, stores: [] }
  const [shopForm, setShopForm] = useState({});       // `${ws._id}:${index}` -> { clientId, clientSecret, shopDomain }
  const [limitDraft, setLimitDraft] = useState({});   // ws._id -> { whatsapp, instagram, facebook, shopify, name }
  const [savingWs, setSavingWs] = useState('');

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('sys_token') || ''}` } });

  const fetchWorkspaces = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/workspaces`, auth());
      setWorkspaces(res.data);
      const entries = await Promise.all(res.data.map((ws) =>
        axios.get(`${API_URL}/api/workspaces/${ws._id}/shopify`, auth()).then((r) => [ws._id, r.data]).catch(() => [ws._id, null])
      ));
      setShopifyByWs(Object.fromEntries(entries));
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem('sys_token'); window.location.href = '/add-workspaces'; }
    } finally { setLoadingList(false); }
  };

  useEffect(() => { fetchWorkspaces(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { window.history.replaceState(null, '', tab === 'analytics' ? '#analytics' : window.location.pathname); }, [tab]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true); setCreateErr(''); setCreated(null);
    try {
      const res = await axios.post(`${API_URL}/api/workspaces`, { name }, auth());
      setCreated({ name, email: res.data.adminCredentials.email, password: res.data.adminCredentials.password });
      setName('');
      fetchWorkspaces();
    } catch { setCreateErr('Error creating workspace'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (ws) => {
    if (!window.confirm(`Delete "${ws.name}" and all linked user access? This cannot be undone.`)) return;
    await axios.delete(`${API_URL}/api/workspaces/${ws._id}`, auth());
    setOpen('');
    fetchWorkspaces();
  };

  const draftFor = (ws) => limitDraft[ws._id] || {
    name: ws.name,
    whatsapp: ws.integrationLimits?.whatsapp ?? 1,
    instagram: ws.integrationLimits?.instagram ?? 1,
    facebook: ws.integrationLimits?.facebook ?? 1,
    shopify: ws.integrationLimits?.shopify ?? 1,
  };
  const setDraft = (ws, patch) => setLimitDraft((prev) => ({ ...prev, [ws._id]: { ...draftFor(ws), ...patch } }));

  const saveWorkspace = async (ws) => {
    const d = draftFor(ws);
    setSavingWs(ws._id);
    try {
      await axios.patch(`${API_URL}/api/workspaces/${ws._id}`, {
        name: d.name,
        integrationLimits: { whatsapp: d.whatsapp, instagram: d.instagram, facebook: d.facebook, shopify: d.shopify },
      }, auth());
      setLimitDraft((prev) => { const n = { ...prev }; delete n[ws._id]; return n; });
      fetchWorkspaces();
    } catch (err) { alert(err.response?.data?.message || 'Save failed'); }
    finally { setSavingWs(''); }
  };

  const saveShopify = async (ws, index) => {
    const key = `${ws._id}:${index}`;
    const f = shopForm[key] || {};
    if (!f.clientId || !f.clientSecret || !f.shopDomain) { alert('Client ID, Client Secret and Shop Domain are all required.'); return; }
    try {
      await axios.post(`${API_URL}/api/workspaces/${ws._id}/shopify-config`, {
        connectionIndex: index,
        clientId: f.clientId.trim(), clientSecret: f.clientSecret.trim(), shopDomain: f.shopDomain.trim().toLowerCase(),
      }, auth());
      setShopForm((prev) => ({ ...prev, [key]: { ...f, clientSecret: '' } }));
      fetchWorkspaces();
    } catch (err) { alert(err.response?.data?.message || 'Failed to save Shopify config'); }
  };

  const stats = useMemo(() => {
    const month = Date.now() - 30 * 864e5;
    const stores = Object.values(shopifyByWs).reduce((s, sh) => s + (sh?.stores || []).filter((x) => x.connected).length, 0);
    return { total: workspaces.length, recent: workspaces.filter((w) => new Date(w.createdAt).getTime() > month).length, stores };
  }, [workspaces, shopifyByWs]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = [...workspaces].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    if (!q) return list;
    return list.filter((w) => [w.name, w.workspaceUuid, w.generatedAdminEmail].some((v) => String(v || '').toLowerCase().includes(q)));
  }, [workspaces, query]);

  return (
    <div className="ma-wrap">
      <style>{css + siteAnalyticsCss}</style>
      <header className="ma-top">
        <div className="ma-brand">
          <img src="/vetobase-icon.png" alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          <div><div className="ma-logo">Vetobase <span>Platform Control</span></div><p className="ma-sub">System administration · {stats.total} workspace{stats.total === 1 ? '' : 's'}</p></div>
        </div>
        <nav className="ma-tabs">
          {TABS.map((t) => (
            <button key={t.key} className={`ma-tab ${tab === t.key ? 'on' : ''}`} onClick={() => setTab(t.key)}>
              {t.label}{t.key === 'workspaces' && <span className="ma-tab-n">{stats.total}</span>}
            </button>
          ))}
        </nav>
        <button className="ma-signout" onClick={() => { localStorage.removeItem('sys_token'); window.location.href = '/add-workspaces'; }}>Sign out</button>
      </header>

      {tab === 'analytics' ? <div className="ma-body"><SiteAnalytics /></div> : (
        <div className="ma-body">
          <div className="ma-stats">
            <div className="ma-stat"><span>Workspaces</span><b>{stats.total}</b></div>
            <div className="ma-stat"><span>Created in the last 30 days</span><b>{stats.recent}</b></div>
            <div className="ma-stat"><span>Shopify stores installed</span><b>{stats.stores}</b></div>
          </div>

          <div className="ma-toolbar">
            <input className="ma-search" placeholder="Search by name, UUID or admin email…" value={query} onChange={(e) => setQuery(e.target.value)} />
            <button className="ma-btn" onClick={() => { setShowCreate((v) => !v); setCreated(null); setCreateErr(''); }}>{showCreate ? 'Close' : '+ New workspace'}</button>
          </div>

          {showCreate && (
            <section className="ma-card ma-create">
              <div><h2 className="ma-card-title">Deploy a new workspace</h2><p className="ma-card-sub">Creates the workspace and its first admin user instantly.</p></div>
              <form onSubmit={handleCreate} className="ma-create-form">
                <input className="ma-input" placeholder="Organization name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
                <button className="ma-btn" type="submit" disabled={loading}>{loading ? 'Creating…' : 'Create workspace'}</button>
              </form>
              {createErr && <div className="ma-err ma-full">{createErr}</div>}
              {created && (
                <div className="ma-banner ma-full">
                  <b>“{created.name}” created.</b> Save these admin credentials:
                  <div className="ma-cred"><span>Email</span><code>{created.email}</code><Copy text={created.email} /></div>
                  <div className="ma-cred"><span>Password</span><code>{created.password}</code><Copy text={created.password} /></div>
                </div>
              )}
            </section>
          )}

          <section className="ma-list">
            <div className="ma-list-h"><span>Workspace</span><span>Admin email</span><span>Channel limits</span><span>Created</span><span /></div>
            {loadingList && <div className="ma-empty">Loading workspaces…</div>}
            {!loadingList && visible.length === 0 && <div className="ma-empty">{query ? 'No workspace matches your search.' : 'No workspaces yet.'}</div>}
            {visible.map((ws) => {
              const sh = shopifyByWs[ws._id];
              const d = draftFor(ws);
              const dirty = !!limitDraft[ws._id];
              const isOpen = open === ws._id;
              const sec = section[ws._id] || 'access';
              const installed = (sh?.stores || []).filter((s) => s.connected).length;
              return (
                <div key={ws._id} className={`ma-row ${isOpen ? 'open' : ''}`}>
                  <button className="ma-row-h" onClick={() => setOpen(isOpen ? '' : ws._id)}>
                    <span className="ma-row-name"><b>{ws.name}</b><code>{ws.workspaceUuid}</code></span>
                    <span className="ma-row-email">{ws.generatedAdminEmail}</span>
                    <span className="ma-chips">
                      {PLATFORMS.map((p) => <i key={p.key} title={p.label}>{p.label.slice(0, 2)} {ws.integrationLimits?.[p.key] ?? 1}</i>)}
                      {installed > 0 && <i className="ok">{installed} store{installed > 1 ? 's' : ''} live</i>}
                    </span>
                    <span className="ma-row-date">{fmtDate(ws.createdAt)}</span>
                    <span className="ma-chev">{isOpen ? '−' : '+'}</span>
                  </button>

                  {isOpen && (
                    <div className="ma-row-body">
                      <div className="ma-subtabs">
                        {[['access', 'Access'], ['settings', 'Name & limits'], ['shopify', `Shopify stores (${(sh?.stores || []).filter((s) => s.configured).length}/${d.shopify})`]].map(([k, l]) => (
                          <button key={k} className={`ma-subtab ${sec === k ? 'on' : ''}`} onClick={() => setSection((p) => ({ ...p, [ws._id]: k }))}>{l}</button>
                        ))}
                        <button className="ma-del" onClick={() => handleDelete(ws)}>Delete workspace</button>
                      </div>

                      {sec === 'access' && (
                        <div className="ma-kv">
                          <div><span className="ma-k">Workspace UUID</span><div className="ma-vrow"><code>{ws.workspaceUuid}</code><Copy text={ws.workspaceUuid} /></div></div>
                          <div><span className="ma-k">Admin email</span><div className="ma-vrow"><code>{ws.generatedAdminEmail}</code><Copy text={ws.generatedAdminEmail} /></div></div>
                          <div><span className="ma-k">Admin password</span><div className="ma-vrow">
                            <code className="ma-pass">{reveal[ws._id] ? ws.generatedAdminPassword : '••••••••••••'}</code>
                            <button className="ma-copy ghost" onClick={() => setReveal((p) => ({ ...p, [ws._id]: !p[ws._id] }))}>{reveal[ws._id] ? 'Hide' : 'Show'}</button>
                            <Copy text={ws.generatedAdminPassword} />
                          </div></div>
                          <div><span className="ma-k">Dashboard</span><div className="ma-vrow"><a className="ma-link" href={`/workspace/${ws.workspaceUuid}/dashboard`} target="_blank" rel="noopener noreferrer">/workspace/{ws.workspaceUuid.slice(0, 8)}…/dashboard ↗</a></div></div>
                        </div>
                      )}

                      {sec === 'settings' && (
                        <div className="ma-section">
                          <label className="ma-field"><span>Workspace name</span>
                            <input className="ma-input-sm" value={d.name} onChange={(e) => setDraft(ws, { name: e.target.value })} />
                          </label>
                          <div className="ma-limits">
                            {PLATFORMS.map((p) => (
                              <label key={p.key} className="ma-limit"><span>{p.label}</span>
                                <input type="number" min="0" max="20" className="ma-num" value={d[p.key]} onChange={(e) => setDraft(ws, { [p.key]: parseInt(e.target.value || '0', 10) })} />
                              </label>
                            ))}
                          </div>
                          <div className="ma-limits-hint">Maximum connections this workspace can hold per channel.</div>
                          <button className={`ma-btn-sm ${dirty ? '' : 'ghost'}`} disabled={!dirty || savingWs === ws._id} onClick={() => saveWorkspace(ws)}>
                            {savingWs === ws._id ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
                          </button>
                        </div>
                      )}

                      {sec === 'shopify' && (
                        <div className="ma-section">
                          {sh?.callbackUrl && (
                            <div className="ma-callback"><span className="ma-k">OAuth callback URL</span>
                              <div className="ma-vrow"><input readOnly value={sh.callbackUrl} className="ma-input-sm ma-mono" /><Copy text={sh.callbackUrl} /></div>
                            </div>
                          )}
                          <div className="ma-stores">
                            {Array.from({ length: Math.max(1, d.shopify) }).map((_, index) => {
                              const store = (sh?.stores || []).find((s) => s.index === index);
                              const key = `${ws._id}:${index}`;
                              const f = shopForm[key] || {};
                              const set = (patch) => setShopForm((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
                              const status = !store ? ['Empty', ''] : store.connected ? ['Installed', 'ok'] : store.configured ? ['Configured', 'warn'] : ['Empty', ''];
                              return (
                                <div key={index} className="ma-store">
                                  <div className="ma-store-h"><span className="ma-store-n">Store {index + 1}{store?.shopDomain ? ` · ${store.shopDomain}` : ''}</span><span className={`ma-pill ${status[1]}`}>{status[0]}</span></div>
                                  <div className="ma-store-grid">
                                    <input className="ma-input-sm" placeholder="Client ID" value={f.clientId ?? store?.clientId ?? ''} onChange={(e) => set({ clientId: e.target.value })} />
                                    <input className="ma-input-sm" placeholder="store.myshopify.com" value={f.shopDomain ?? store?.shopDomain ?? ''} onChange={(e) => set({ shopDomain: e.target.value })} />
                                    <input className="ma-input-sm ma-span2" type="password" placeholder="Client Secret (hidden after save)" value={f.clientSecret || ''} onChange={(e) => set({ clientSecret: e.target.value })} />
                                    <button className="ma-btn-sm ma-span2" onClick={() => saveShopify(ws, index)}>Save store {index + 1}</button>
                                  </div>
                                  {store?.webhookRegistrationError && <div className="ma-err">Webhook error: {store.webhookRegistrationError}</div>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        </div>
      )}
    </div>
  );
};

const css = `
.ma-wrap{ min-height:100vh; background:#F9F4EE; padding:0 0 64px; color:#111111; font-family:'Space Grotesk',system-ui,sans-serif; }
.ma-top{ position:sticky; top:0; z-index:20; display:flex; align-items:center; gap:24px; padding:16px 40px; background:rgba(249,244,238,.92); backdrop-filter:blur(8px); border-bottom:1px solid #E7E1D6; flex-wrap:wrap; }
.ma-brand{ display:flex; align-items:center; gap:12px; }
.ma-brand img{ width:38px; height:38px; border-radius:11px; background:#fff; border:1px solid #E7E1D6; padding:3px; }
.ma-logo{ font-size:18px; font-weight:800; letter-spacing:-0.03em; }
.ma-logo span{ font-weight:500; color:#6B6862; margin-left:4px; }
.ma-sub{ color:#9C978C; font-size:12px; margin:2px 0 0; }
.ma-tabs{ display:flex; gap:6px; background:#fff; border:1px solid #E7E1D6; border-radius:999px; padding:4px; margin-left:auto; }
.ma-tab{ border:none; background:none; padding:8px 18px; border-radius:999px; font:inherit; font-size:13.5px; font-weight:700; color:#3A3A37; cursor:pointer; display:inline-flex; align-items:center; gap:7px; }
.ma-tab.on{ background:#111; color:#fff; }
.ma-tab-n{ font-size:11px; background:#F3EEE6; color:#111; border-radius:99px; padding:1px 7px; }
.ma-tab.on .ma-tab-n{ background:#fff; }
.ma-signout{ background:#fff; border:1px solid #E7E1D6; color:#111111; padding:8px 16px; border-radius:999px; font-weight:600; font-size:13px; cursor:pointer; }
.ma-signout:hover{ background:#111111; color:#fff; border-color:#111111; }
.ma-wrap > .sa, .ma-body{ max-width:1360px; margin:26px auto 0; padding:0 40px; }
.ma-body{ display:flex; flex-direction:column; gap:16px; }

.ma-stats{ display:grid; grid-template-columns:repeat(3,1fr); gap:12px; }
.ma-stat{ background:#fff; border:1px solid #E7E1D6; border-radius:16px; padding:16px 18px; }
.ma-stat span{ font-size:12.5px; color:#6B6862; font-weight:600; }
.ma-stat b{ display:block; font-size:28px; font-weight:800; letter-spacing:-0.04em; margin-top:2px; }
.ma-toolbar{ display:flex; gap:10px; }
.ma-search{ flex:1; padding:11px 16px; border:1px solid #E7E1D6; border-radius:999px; font:inherit; font-size:14px; background:#fff; outline:none; }
.ma-search:focus{ border-color:#111; }

.ma-card{ background:#fff; border:1px solid #E7E1D6; border-radius:16px; padding:20px 22px; }
.ma-create{ display:flex; align-items:center; justify-content:space-between; gap:20px; flex-wrap:wrap; }
.ma-card-title{ font-size:16px; font-weight:800; margin:0; letter-spacing:-0.02em; }
.ma-card-sub{ font-size:12.5px; color:#9C978C; margin:3px 0 0; }
.ma-create-form{ display:flex; gap:10px; flex:1; min-width:300px; justify-content:flex-end; }
.ma-input{ flex:1; max-width:380px; padding:11px 14px; border:1px solid #E7E1D6; border-radius:10px; font:inherit; font-size:14px; outline:none; }
.ma-input:focus{ border-color:#111111; }
.ma-btn{ background:#111111; color:#fff; border:none; padding:11px 20px; border-radius:999px; box-shadow:4px 4px 0 rgba(17,17,17,.2); font:inherit; font-weight:700; font-size:14px; cursor:pointer; white-space:nowrap; }
.ma-btn:hover{ background:#000000; } .ma-btn:disabled{ opacity:.6; }
.ma-full{ flex-basis:100%; }
.ma-banner{ background:#FAF7F1; border:1px solid #E7E1D6; padding:14px 16px; border-radius:12px; font-size:13px; display:flex; flex-direction:column; gap:8px; }
.ma-cred{ display:grid; grid-template-columns:80px 1fr auto; gap:10px; align-items:center; }
.ma-cred span{ font-size:11px; font-weight:800; text-transform:uppercase; letter-spacing:.05em; color:#9C978C; }
.ma-cred code{ font-family:ui-monospace,Menlo,monospace; font-size:13px; word-break:break-all; }

.ma-list{ background:#fff; border:1px solid #E7E1D6; border-radius:18px; overflow:hidden; }
.ma-list-h, .ma-row-h{ display:grid; grid-template-columns:minmax(220px,1.6fr) minmax(180px,1.2fr) minmax(220px,1.4fr) 120px 36px; gap:14px; align-items:center; }
.ma-list-h{ padding:11px 20px; font-size:11px; font-weight:800; text-transform:uppercase; letter-spacing:.05em; color:#9C978C; border-bottom:1px solid #E7E1D6; background:#FCFAF6; }
.ma-empty{ padding:28px; text-align:center; color:#9C978C; font-size:14px; }
.ma-row{ border-bottom:1px solid #F1ECE3; } .ma-row:last-child{ border-bottom:none; }
.ma-row.open{ background:#FCFAF6; }
.ma-row-h{ width:100%; padding:14px 20px; background:none; border:none; font:inherit; color:inherit; text-align:left; cursor:pointer; }
.ma-row-h:hover{ background:#FAF7F1; }
.ma-row-name{ display:flex; flex-direction:column; gap:2px; min-width:0; }
.ma-row-name b{ font-size:14.5px; }
.ma-row-name code, .ma-row-email{ font-family:ui-monospace,Menlo,monospace; font-size:11.5px; color:#9C978C; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.ma-row-email{ color:#3A3A37; font-size:12.5px; }
.ma-chips{ display:flex; gap:5px; flex-wrap:wrap; }
.ma-chips i{ font-style:normal; font-size:11px; font-weight:700; background:#F3EEE6; border-radius:99px; padding:2px 8px; color:#3A3A37; }
.ma-chips i.ok{ background:#E3F2EC; color:#0F6E56; }
.ma-row-date{ font-size:12.5px; color:#6B6862; }
.ma-chev{ width:28px; height:28px; border-radius:50%; border:1px solid #E7E1D6; display:grid; place-items:center; font-weight:700; background:#fff; }
.ma-row-body{ padding:4px 20px 20px; }
.ma-subtabs{ display:flex; gap:6px; align-items:center; border-bottom:1px solid #E7E1D6; margin-bottom:16px; flex-wrap:wrap; }
.ma-subtab{ border:none; background:none; font:inherit; font-size:13px; font-weight:700; color:#6B6862; padding:10px 12px; cursor:pointer; border-bottom:2px solid transparent; margin-bottom:-1px; }
.ma-subtab.on{ color:#111; border-bottom-color:#111; }
.ma-del{ margin-left:auto; background:none; border:none; color:#B23A3A; font:inherit; font-size:12.5px; font-weight:700; cursor:pointer; padding:10px 4px; }
.ma-del:hover{ text-decoration:underline; }

.ma-kv{ display:grid; grid-template-columns:1fr 1fr; gap:14px 22px; background:#fff; border:1px solid #E7E1D6; border-radius:14px; padding:16px; }
.ma-k{ display:block; font-size:10.5px; font-weight:800; text-transform:uppercase; letter-spacing:.06em; color:#9C978C; margin-bottom:5px; }
.ma-vrow{ display:flex; gap:8px; align-items:center; }
.ma-vrow code{ flex:1; font-family:ui-monospace,Menlo,monospace; font-size:12.5px; background:#FAF7F1; border:1px solid #EFE9DF; border-radius:8px; padding:7px 10px; word-break:break-all; }
.ma-pass{ color:#B23A3A; }
.ma-link{ color:#111; font-weight:700; font-size:13px; }
.ma-copy{ padding:7px 12px; background:#111111; color:#fff; border:none; border-radius:8px; font:inherit; font-weight:700; font-size:12px; cursor:pointer; white-space:nowrap; }
.ma-copy.ghost{ background:#F3EEE6; color:#111; }
.ma-mono{ font-family:ui-monospace,Menlo,monospace; }

.ma-section{ background:#fff; border:1px solid #E7E1D6; border-radius:14px; padding:16px; }
.ma-field{ display:flex; flex-direction:column; gap:5px; margin-bottom:12px; max-width:420px; }
.ma-field > span{ font-size:11px; font-weight:700; color:#6B6862; }
.ma-input-sm{ padding:8px 11px; border:1px solid #E7E1D6; border-radius:8px; font:inherit; font-size:13px; outline:none; width:100%; box-sizing:border-box; background:#fff; }
.ma-input-sm:focus{ border-color:#111111; }
.ma-limits{ display:grid; grid-template-columns:repeat(4,minmax(0,120px)); gap:10px; }
.ma-limit{ display:flex; flex-direction:column; gap:5px; }
.ma-limit > span{ font-size:11px; font-weight:700; color:#3A3A37; }
.ma-num{ padding:8px; border:1px solid #E7E1D6; border-radius:8px; font:inherit; font-size:14px; font-weight:700; text-align:center; outline:none; }
.ma-num:focus{ border-color:#111111; }
.ma-limits-hint{ font-size:11.5px; color:#9C978C; margin:8px 0 12px; }
.ma-btn-sm{ background:#111111; color:#fff; border:none; padding:8px 16px; border-radius:999px; font:inherit; font-weight:700; font-size:12.5px; cursor:pointer; }
.ma-btn-sm:hover{ background:#000000; }
.ma-btn-sm.ghost{ background:#FAF7F1; color:#9C978C; cursor:default; }
.ma-btn-sm:disabled{ opacity:.7; }
.ma-callback{ margin-bottom:14px; max-width:720px; }
.ma-stores{ display:grid; grid-template-columns:repeat(auto-fill,minmax(320px,1fr)); gap:12px; }
.ma-store{ background:#FAF7F1; border:1px solid #E7E1D6; border-radius:12px; padding:12px 13px; }
.ma-store-h{ display:flex; align-items:center; justify-content:space-between; margin-bottom:9px; gap:8px; }
.ma-store-n{ font-size:12.5px; font-weight:700; }
.ma-pill{ font-size:10.5px; font-weight:800; padding:2px 9px; border-radius:20px; background:#fff; color:#6B6862; border:1px solid #E7E1D6; }
.ma-pill.ok{ background:#E3F2EC; color:#0F6E56; border-color:#CDE7DC; } .ma-pill.warn{ background:#FFF3D6; color:#8A5A00; border-color:#F4E2B5; }
.ma-store-grid{ display:grid; grid-template-columns:1fr 1fr; gap:7px; }
.ma-span2{ grid-column:1 / 3; }
.ma-err{ margin-top:7px; font-size:12px; color:#c0392b; background:#fdecea; border-radius:8px; padding:7px 10px; }

@media (max-width:1000px){ .ma-list-h{ display:none; } .ma-row-h{ grid-template-columns:1fr 36px; } .ma-row-email,.ma-chips,.ma-row-date{ display:none; } .ma-kv{ grid-template-columns:1fr; } }
@media (max-width:680px){ .ma-top{ position:static; padding:12px 16px; } .ma-tabs{ margin-left:0; order:3; width:100%; justify-content:center; } .ma-wrap > .sa, .ma-body{ padding:0 16px; } .ma-stats{ grid-template-columns:1fr; } .ma-limits{ grid-template-columns:repeat(2,1fr); } .ma-toolbar{ flex-direction:column; } }
`;

export default MasterAdmin;
